import type { Verification } from './vocabulary';

/**
 * A material value together with the provenance that makes it admissible.
 *
 * This shape is the reason `EvidenceValue` cannot be handed a bare number: the
 * constraint "never present a value without its provenance" is expressed as a
 * type, so violating it is a compile error rather than a review comment.
 *
 * `raw` and `normalized` are kept separately and permanently. A printed value
 * is never replaced by its interpretation — both are stored and the
 * relationship between them is shown (see `EvidenceCard`'s last row).
 *
 * `normalized === null` means «нет данных»: the archive was searched and is
 * silent. It is a legitimate, common result, not a loading state and not an
 * error.
 */
export interface Fact<T> {
  /** Exactly as the page printed it, including the original separators. */
  readonly raw: string;
  /** Machine-comparable form, or null when the archive has no value. */
  readonly normalized: T | null;
  readonly unit: string | null;
  readonly verification: Verification;

  readonly sourceFileId: string;
  readonly pageId: string;
  readonly evidenceRegionId: string;
  readonly extractionRunId: string;
  readonly datasetVersionId: string;

  /** Human-facing citation, e.g. `1982 · с. 118 · табл. 4`. */
  readonly citation: string;
}

/** True when the archive holds no value for this fact. */
export function isAbsent<T>(fact: Fact<T>): boolean {
  return fact.normalized === null || fact.verification === 'unknown';
}

/**
 * True when a fact may be quoted as established truth.
 *
 * Deliberately narrow: candidates are machine extractions that no human has
 * looked at, and a conflict has no single value to quote. Callers that need to
 * *display* a candidate still may — they just may not present it as settled.
 */
export function isEstablished<T>(fact: Fact<T>): boolean {
  return fact.verification === 'verified' || fact.verification === 'reviewed_supported';
}
